import Decimal from 'decimal.js';
import { useCallback, useMemo, useState } from 'react';

import { currencies, defaults, getCurrencyConfig, periods, widgetConfig } from '../../config/config';
import { Currency } from '../../types/currency';
import {
  collateralBtcToLoan,
  liquidationPrice,
  loanToCollateralBtc,
  maturityRepayment,
} from '../utils/calculatorMath';
import { lookupInterestRateDecimal, isApproximateRate } from '../utils/rateLookup';
import { useIsMobile } from './useIsMobile';

interface Options {
  initialCurrency?: string;
}

const BTC_DECIMALS = 5;
const FIAT_DECIMALS = 0;

function parseAmount(raw: string): Decimal | null {
  // Strip thousands separators (commas, spaces, narrow-no-break-space).
   
  const cleaned = raw.replace(/[,\s  ]/g, '');
  if (cleaned === '' || cleaned === '.') return null;
  try {
    const value = new Decimal(cleaned);
    return value.lt(0) ? null : value;
  } catch {
    return null;
  }
}

const FIAT_FORMATTER = new Intl.NumberFormat('en-US', {
  maximumFractionDigits: FIAT_DECIMALS,
  minimumFractionDigits: 0,
});

function formatFiat(value: Decimal): string {
  return FIAT_FORMATTER.format(Number(value.toFixed(FIAT_DECIMALS)));
}

function formatBtc(value: Decimal): string {
  return value.toFixed(BTC_DECIMALS);
}

function resolveInitialCurrency(raw?: string): Currency {
  if (!raw) return defaults.currency;
  const match = currencies.find(c => c.toLowerCase() === raw.toLowerCase());
  return match ?? defaults.currency;
}

export function useLoanCalculator({ initialCurrency }: Options) {
  const isMobile = useIsMobile();
  const [currency, setCurrencyState] = useState<Currency>(() =>
    resolveInitialCurrency(initialCurrency)
  );
  const [period, setPeriod] = useState<number>(defaults.period);

  const config = useMemo(() => getCurrencyConfig(currency)!, [currency]);
  const btcPrice = useMemo(() => new Decimal(config.bitcoinPrice), [config]);
  const minLoan = useMemo(() => new Decimal(config.minTransactionLimit), [config]);
  const maxLoan = useMemo(() => new Decimal(config.maxTransactionLimit), [config]);
  const minBtc = useMemo(() => loanToCollateralBtc(minLoan, btcPrice), [minLoan, btcPrice]);
  const maxBtc = useMemo(() => loanToCollateralBtc(maxLoan, btcPrice), [maxLoan, btcPrice]);

  // Authoritative loan amount as Decimal. Collateral is always derived.
  const [loanBig, setLoanBig] = useState<Decimal>(() => {
    const seed = minLoan.mul(15); // arbitrary nicer-looking starting point
    return seed.gt(maxLoan) ? maxLoan : seed;
  });
  const [loanInput, setLoanInput] = useState<string>(() => formatFiat(loanBig));
  const [collateralInput, setCollateralInput] = useState<string>(() =>
    formatBtc(loanToCollateralBtc(loanBig, btcPrice))
  );

  const updateFromLoan = useCallback(
    (next: Decimal) => {
      const clamped = next.lt(minLoan) ? minLoan : next.gt(maxLoan) ? maxLoan : next;
      setLoanBig(clamped);
      setLoanInput(formatFiat(clamped));
      setCollateralInput(formatBtc(loanToCollateralBtc(clamped, btcPrice)));
    },
    [minLoan, maxLoan, btcPrice]
  );

  const updateFromCollateral = useCallback(
    (nextBtc: Decimal) => {
      const clampedBtc = nextBtc.lt(minBtc) ? minBtc : nextBtc.gt(maxBtc) ? maxBtc : nextBtc;
      const loan = collateralBtcToLoan(clampedBtc, btcPrice);
      setLoanBig(loan);
      setLoanInput(formatFiat(loan));
      setCollateralInput(formatBtc(clampedBtc));
    },
    [minBtc, maxBtc, btcPrice]
  );

  const onLoanInputChange = useCallback((raw: string) => {
    // remove all non-digit chars
    const digitsOnly = raw.replace(/\D/g, '');
    const parsed = parseAmount(digitsOnly);
    if (parsed && parsed.gt(maxLoan)) {
      setLoanBig(maxLoan);
      setLoanInput(formatFiat(maxLoan));
      setCollateralInput(formatBtc(loanToCollateralBtc(maxLoan, btcPrice)));
      return;
    }
    // Route the display string through the Decimal-typed formatter so the only
    // number-domain crossing is at Intl.NumberFormat inside formatFiat.
    setLoanInput(parsed === null ? '' : formatFiat(parsed));
    if (parsed === null) return;
    setLoanBig(parsed);
    setCollateralInput(formatBtc(loanToCollateralBtc(parsed, btcPrice)));
  }, [btcPrice, maxLoan]);

  const onCollateralInputChange = useCallback((raw: string) => {
    // allow only digits and one decimal separator
    const digitsAndDots = raw.replace(/[^\d.]/g, '');
    const [intPart, ...rest] = digitsAndDots.split('.');
    const filtered = rest.length === 0 ? intPart : `${intPart}.${rest.join('')}`;
    const parsed = parseAmount(filtered);
    if (parsed && parsed.gt(maxBtc)) {
      const loan = collateralBtcToLoan(maxBtc, btcPrice);
      setLoanBig(loan);
      setLoanInput(formatFiat(loan));
      setCollateralInput(formatBtc(maxBtc));
      return;
    }
    setCollateralInput(filtered);
    if (parsed === null) return;
    const loan = collateralBtcToLoan(parsed, btcPrice);
    setLoanBig(loan);
    setLoanInput(formatFiat(loan));
  }, [btcPrice, maxBtc]);

  const onCollateralBlur = useCallback(() => {
    const parsed = parseAmount(collateralInput);
    updateFromCollateral(parsed ?? minBtc);
  }, [collateralInput, minBtc, updateFromCollateral]);

  const setCurrency = useCallback(
    (next: Currency) => {
      setCurrencyState(next);
      const nextConfig = getCurrencyConfig(next)!;
      const nextBtcPrice = new Decimal(nextConfig.bitcoinPrice);
      const nextMin = new Decimal(nextConfig.minTransactionLimit);
      const nextMax = new Decimal(nextConfig.maxTransactionLimit);
      const clamped = loanBig.lt(nextMin) ? nextMin : loanBig.gt(nextMax) ? nextMax : loanBig;
      setLoanBig(clamped);
      setLoanInput(formatFiat(clamped));
      setCollateralInput(formatBtc(loanToCollateralBtc(clamped, nextBtcPrice)));
    },
    [loanBig]
  );

  const interestRate = useMemo(() => lookupInterestRateDecimal(config, period), [config, period]);
  const rateIsApproximate = useMemo(() => isApproximateRate(config, period), [config, period]);

  const collateralBig = useMemo(
    () => loanToCollateralBtc(loanBig, btcPrice),
    [loanBig, btcPrice]
  );

  const liquidationPriceValue = useMemo(
    () => liquidationPrice(loanBig, collateralBig),
    [loanBig, collateralBig]
  );

  const maturityRepaymentValue = useMemo(
    () => maturityRepayment(loanBig, interestRate, period),
    [loanBig, interestRate, period]
  );

  const onLoanBlur = useCallback(() => {
    const parsed = parseAmount(loanInput);
    updateFromLoan(parsed ?? minLoan);
  }, [loanInput, minLoan, updateFromLoan]);

  return {
    // state (display)
    currency,
    loanInput,
    collateralInput,
    period,
    isMobile,

    // authoritative
    loanBig,
    collateralBig,
    btcPrice,
    config,
    minLoan,
    maxLoan,
    minBtc,
    maxBtc,
    periods,

    // outputs
    interestRate,
    rateIsApproximate,
    liquidationPriceValue,
    maturityRepaymentValue,
    originationFeeRate: widgetConfig.originationFeeRate,

    // actions
    setCurrency,
    setPeriod,
    onLoanInputChange,
    onLoanBlur,
    onCollateralInputChange,
    onCollateralBlur,
    setLoanFromSlider: updateFromLoan,
    setCollateralFromSlider: updateFromCollateral,
  };
}
